/** Entity history grouped by local day, with expandable evidence and exact recorded times.
 * Presentational: its feature supplies events, summary and evidence navigation.
 */
import { RefreshCw } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { CaseEventKind } from '@/services/api/case-events'
import { formatDate } from '@/utils/date'
import { EntityHistoryHeader, type EntityHistoryHeaderView } from './EntityHistoryHeader'
import type { FactChangeRow } from './fact-row-text'
import { HistoryEntry } from './HistoryEntry'
import type { HistoryActions, HistoryEventDetail } from './history-detail'
import type { IdentityChange } from './identity-row-text'
import type { PropertyChangeRow } from './row-text'

const HISTORY = 'investigations.entityDetail.history'

/** One event as the card shows it. */
export interface EntityHistoryRow {
  id: string
  /** The event's kind; null for one this build does not know (a newer server). */
  kind: CaseEventKind | null
  documentId: string | null
  documentName: string | null
  detail?: HistoryEventDetail
  /** When the case learned it; ISO 8601. */
  learnedAt: string
  /** A property change's key and values (`property_changed`). */
  property?: PropertyChangeRow | null
  /** The name of the entity a mention arrived under, when one merged into this one (ADR-0091). */
  alias?: string | null
  /** A merge, its undo or a dismissed match (`entities_merged`, `merge_reverted`, `pair_dismissed`). */
  identity?: IdentityChange | null
  /** An analyst's mark or its undo (`fact_asserted`, `fact_invalidated`, an analyst's `entity_retired`). */
  fact?: FactChangeRow | null
}

export type EntityHistoryView =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; rows: readonly EntityHistoryRow[] }

export interface EntityHistoryCardProps {
  view: EntityHistoryView
  /** The header: first and last seen, sources, activity, last change, conflicts (R-C1); null while unread. */
  summary?: EntityHistoryHeaderView | null
  actions: HistoryActions
  onRetry: () => void
}

function Loading() {
  const { t } = useTranslation()
  return (
    <div role="status" className="flex flex-col gap-1">
      <span className="sr-only">{t(`${HISTORY}.loading`)}</span>
      <Skeleton aria-hidden className="h-7 w-full" />
      <Skeleton aria-hidden className="h-7 w-full" />
    </div>
  )
}

function Failed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2">
      <p className="text-2xs text-destructive">{t(`${HISTORY}.loadError`)}</p>
      <Button type="button" variant="outline" size="xs" onClick={onRetry}>
        <RefreshCw aria-hidden />
        {t(`${HISTORY}.retry`)}
      </Button>
    </div>
  )
}

function Body({ view, actions, onRetry }: EntityHistoryCardProps) {
  const { t, i18n } = useTranslation()
  if (view.status === 'loading') return <Loading />
  if (view.status === 'failed') return <Failed onRetry={onRetry} />
  if (view.rows.length === 0) {
    return <p className="text-2xs text-muted-foreground">{t(`${HISTORY}.empty`)}</p>
  }
  return (
    <div className="space-y-4">
      {groupHistoryDays(view.rows, i18n.language).map(group => (
        <section key={group.day} aria-label={group.day} className="space-y-1.5">
          <h4 className="text-xs font-medium text-muted-foreground">{group.day}</h4>
          <ul className="flex flex-col gap-1.5">
            {group.rows.map(row => (
              <li key={row.id} data-event-kind={row.kind ?? 'unknown'}>
                <HistoryEntry row={row} actions={actions} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

export function EntityHistoryCard(props: EntityHistoryCardProps) {
  const { t } = useTranslation()
  const headingId = useId()
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1">
      <h3
        id={headingId}
        className="text-4xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80"
      >
        {t(`${HISTORY}.title`)}
      </h3>
      {props.summary && <EntityHistoryHeader view={props.summary} />}
      <Body {...props} />
    </section>
  )
}

/** Group by the displayed local day; retain server order, including tied timestamps. */
export function groupHistoryDays(rows: readonly EntityHistoryRow[], language: string) {
  const groups: { day: string; rows: EntityHistoryRow[] }[] = []
  for (const row of rows) {
    const day = formatDate(row.learnedAt, language)
    const previous = groups.at(-1)
    if (previous?.day === day) previous.rows.push(row)
    else groups.push({ day, rows: [row] })
  }
  return groups
}
